/**
 * The structural slice of a TanStack route the walker reads. Typed deliberately wider
 * than `AnyRoute`: `RouteOptions`' public type doesn't declare `path` (it exists at
 * runtime on every file-based route), so the walker reads through this view instead of
 * casting the route object.
 */
type RouteLike = {
  options: { path?: string; getParentRoute?: () => RouteLike | undefined }
}

/**
 * Derives the legacy route record (`{ [routeName]: urlPattern }`) from the native route
 * objects themselves — one source of truth: a route added to a domain's native routes
 * contributes its pattern here automatically, so the totem mapping
 * (`RPPCore.getTotemInfoByRoute` reads this record pattern → tool) can never drift from
 * the live tree. TanStack `$param` segments convert to the legacy `:param` form; the
 * root route's `/` is dropped.
 *
 * Consumers (getTotemInfoByRoute, domainsForLoad, getRoutes) only care that every VALUE
 * is a correct pattern and every KEY is unique — the key is not the domain's route name
 * (those live in the boot route catalog). So the record is keyed by the route's last
 * segment, falling back to the pattern itself when segments collide or a pathless route
 * would produce an empty key: a dropped entry here means a route silently loses its
 * totem mapping and catalog loading.
 */
export const legacyRoutePatterns = (routes: readonly RouteLike[]): Record<string, string> => {
  const seen = new Set<string>()
  return Object.fromEntries(
    routes.map(route => {
      const segments: string[] = []
      let current: RouteLike | undefined = route
      while (current) {
        const path = current.options.path
        if (path && path !== '/') {
          // Lone `$` (splat) has no name to convert — the legacy wildcard is `*`.
          segments.unshift(path.replace(/\$(\w+)/g, ':$1').replace(/(^|\/)\$($|\/)/, '$1*$2'))
        }
        current = current.options.getParentRoute?.()
      }
      // A segment may carry a leading `/`; collapse so the join can't produce `//`
      // (matchPath would silently fail on it).
      const pattern = `/${segments.join('/')}`.replace(/\/{2,}/g, '/')
      const name = segments[segments.length - 1]?.split('/').pop() ?? ''
      const key = name && !seen.has(name) ? name : pattern
      seen.add(key)
      return [key, pattern]
    })
  )
}
